import { Controller } from "@hotwired/stimulus"
import { parseCsv } from "../scripts/validate_csv"

export default class extends Controller {
  static targets = [
    "fileInput",
    "form",
    "mappingSection",
    "columnsContainer",
    "rowCount",
    "errorMessage",
    "errorText",
    "saveButton",
    "nameColumnInput",
    "emailColumnInput",
    "exampleFieldTemplate",
  ]

  static values = {
    cancelUrl: String,
  }

  connect() {
    this.headers = []
    this.dataRows = []
    this.rowCountValue = 0
    this.updateSaveButtonState()
  }

  fileSelected(event) {
    const file = event.currentTarget.files?.[0]
    this.clearError()

    if (!file) {
      this.resetMapping()
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result || "")).filter((row) => {
          return row.some((cell) => String(cell || "").trim() !== "")
        })
        if (rows.length === 0) {
          this.showError("The CSV file appears to be empty.")
          this.resetMapping()
          return
        }

        this.headers = rows[0].map((header, index) => header.toString().trim() || `Column ${index + 1}`)
        this.dataRows = rows.slice(1)
        this.rowCountValue = this.dataRows.length
        this.renderColumns()
        this.mappingSectionTarget.classList.remove("d-none")
        this.rowCountTarget.textContent = `${this.rowCountValue} ${this.rowCountValue === 1 ? "row" : "rows"} found`
        this.autoSelectNameAndEmail()
        this.initializeNameColumnTooltips()
        this.updateSaveButtonState()
      } catch (error) {
        console.error(error)
        this.showError(error.userMessage || "Unable to read that CSV file. Please check the formatting and try again.")
        this.resetMapping()
      }
    }
    reader.onerror = () => {
      this.showError("Unable to read that CSV file.")
      this.resetMapping()
    }
    reader.readAsText(file)
  }

  renderColumns() {
    this.columnsContainerTarget.innerHTML = ""

    this.headers.forEach((header, index) => {
      const card = document.createElement("div")
      card.className = "forms-import-column-card fanword-border fanword-border-radius p-3 mb-3 w-75"
      card.dataset.columnIndex = String(index)

      const canUseAsName = this.columnHasCompleteValues(index)
      const nameActionHtml = canUseAsName
        ? `<button type="button"
                   class="text-primary text-decoration-underline bg-transparent border-0 p-0"
                   data-identity-action="name"
                   data-action="click->forms-import#useAsName">
             Use as Name
           </button>`
        : `<span class="color-fanword-text text-decoration-underline"
                 style="cursor: not-allowed;"
                 data-identity-action="name"
                 data-name-column-tooltip="true"
                 data-bs-toggle="tooltip"
                 data-bs-placement="top"
                 title="Not every row has a value in this column"
                 aria-disabled="true"
                 role="button">
             Use as Name
           </span>`

      card.innerHTML = `
        <div class="d-flex flex-row align-items-start justify-content-between gap-3 mb-3">
          <div class="flex-grow-1">
            <div class="d-flex flex-row align-items-center gap-2 flex-wrap mb-1">
              <p class="color-fanword-text mb-0">Column ${index + 1}</p>
              <span class="fw-pill fw-pill--positive font-fanword-bold d-none"
                    data-identity-pill="name"
                    data-action="click->forms-import#clearNameColumn"
                    title="Click to clear"
                    role="button"
                    style="cursor: pointer;">Name</span>
              <span class="fw-pill fw-pill--neutral font-fanword-bold d-none"
                    data-identity-pill="email"
                    data-action="click->forms-import#clearEmailColumn"
                    title="Click to clear"
                    role="button"
                    style="cursor: pointer;">Email</span>
              <span class="forms-import-identity-actions d-flex flex-row align-items-center gap-2">
                ${nameActionHtml}
                <button type="button"
                        class="text-primary text-decoration-underline bg-transparent border-0 p-0"
                        data-identity-action="email"
                        data-action="click->forms-import#useAsEmail">
                  Use as Email
                </button>
              </span>
            </div>
            <h3 class="fanword-title mb-0">${this.escapeHtml(header)}</h3>
          </div>
          <div class="fw-checkbox text-nowrap" data-import-checkbox-wrapper="true">
            <input type="hidden" name="columns[${index}][index]" value="${index}">
            <input type="hidden" name="columns[${index}][text]" value="${this.escapeAttribute(header)}">
            <input type="checkbox"
                   class="fw-checkbox__input"
                   name="columns[${index}][import]"
                   value="true"
                   checked
                   id="forms-import-import-${index}"
                   data-import-checkbox="true"
                   data-action="change->forms-import#updateSaveButtonState">
            <label class="fw-checkbox__label" for="forms-import-import-${index}">Import as question</label>
          </div>
        </div>

        <div class="d-flex flex-row align-items-center gap-4 flex-wrap mb-3" data-settings-section="true">
          <div class="color-fanword-text">Settings:</div>
          <div class="fw-checkbox">
            <input type="checkbox"
                   class="fw-checkbox__input"
                   name="columns[${index}][spell_check]"
                   value="true"
                   id="forms-import-spell-${index}">
            <label class="fw-checkbox__label" for="forms-import-spell-${index}">Spell check</label>
          </div>
          <div class="fw-checkbox">
            <input type="checkbox"
                   class="fw-checkbox__input"
                   name="columns[${index}][grammar_check]"
                   value="true"
                   id="forms-import-grammar-${index}">
            <label class="fw-checkbox__label" for="forms-import-grammar-${index}">Grammar check</label>
          </div>
          <div class="fw-checkbox">
            <input type="checkbox"
                   class="fw-checkbox__input"
                   name="columns[${index}][example_enabled]"
                   value="true"
                   id="forms-import-example-enabled-${index}"
                   data-action="change->forms-import#toggleExampleField">
            <label class="fw-checkbox__label" for="forms-import-example-enabled-${index}">Example formatting</label>
          </div>
        </div>

        <div class="d-none mb-0" data-example-section="true"></div>
      `

      const exampleSection = card.querySelector('[data-example-section="true"]')
      if (exampleSection && this.hasExampleFieldTemplateTarget) {
        const exampleField = this.exampleFieldTemplateTarget.content.cloneNode(true)
        const exampleId = `forms-import-example-${index}`
        const exampleName = `columns[${index}][example]`
        const label = exampleField.querySelector("label")
        const input = exampleField.querySelector("input")

        if (label) label.setAttribute("for", exampleId)
        if (input) {
          input.id = exampleId
          input.name = exampleName
        }

        exampleSection.appendChild(exampleField)
      }

      this.columnsContainerTarget.appendChild(card)
    })
  }

  initializeNameColumnTooltips() {
    if (!window.bootstrap?.Tooltip) return

    this.columnsContainerTarget.querySelectorAll('[data-name-column-tooltip="true"]').forEach((element) => {
      window.bootstrap.Tooltip.getInstance(element)?.dispose()
      new window.bootstrap.Tooltip(element, { container: "body", trigger: "hover" })
    })
  }

  useAsName(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-column-index]")
    if (!card) return

    const selectedIndex = card.dataset.columnIndex
    if (!this.columnHasCompleteValues(Number(selectedIndex))) {
      this.showError("Name columns must have a value in every row.")
      return
    }

    this.clearError()
    this.nameColumnInputTarget.value = selectedIndex

    if (this.emailColumnInputTarget.value === selectedIndex) {
      this.emailColumnInputTarget.value = ""
    }

    this.syncIdentityColumns()
    this.updateSaveButtonState()
  }

  useAsEmail(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-column-index]")
    if (!card) return

    const selectedIndex = card.dataset.columnIndex
    this.emailColumnInputTarget.value = selectedIndex

    if (this.nameColumnInputTarget.value === selectedIndex) {
      this.nameColumnInputTarget.value = ""
    }

    this.syncIdentityColumns()
    this.updateSaveButtonState()
  }

  clearNameColumn(event) {
    event.preventDefault()
    this.nameColumnInputTarget.value = ""
    this.syncIdentityColumns()
    this.updateSaveButtonState()
  }

  clearEmailColumn(event) {
    event.preventDefault()
    this.emailColumnInputTarget.value = ""
    this.syncIdentityColumns()
    this.updateSaveButtonState()
  }

  toggleExampleField(event) {
    const card = event.currentTarget.closest("[data-column-index]")
    const exampleSection = card?.querySelector('[data-example-section="true"]')
    if (!exampleSection) return
    exampleSection.classList.toggle("d-none", !event.currentTarget.checked)
  }

  syncIdentityColumns() {
    const nameIndex = this.nameColumnInputTarget.value
    const emailIndex = this.emailColumnInputTarget.value

    this.columnsContainerTarget.querySelectorAll("[data-column-index]").forEach((card) => {
      const index = card.dataset.columnIndex
      const isName = index === nameIndex
      const isEmail = index === emailIndex
      const isIdentity = isName || isEmail

      const importCheckbox = card.querySelector('[data-import-checkbox="true"]')
      const importCheckboxWrapper = card.querySelector('[data-import-checkbox-wrapper="true"]')
      const settingsSection = card.querySelector('[data-settings-section="true"]')
      const exampleSection = card.querySelector('[data-example-section="true"]')
      const namePill = card.querySelector('[data-identity-pill="name"]')
      const emailPill = card.querySelector('[data-identity-pill="email"]')
      const nameAction = card.querySelector('[data-identity-action="name"]')
      const emailAction = card.querySelector('[data-identity-action="email"]')

      if (namePill) namePill.classList.toggle("d-none", !isName)
      if (emailPill) emailPill.classList.toggle("d-none", !isEmail)
      if (nameAction) nameAction.classList.toggle("d-none", isName)
      if (emailAction) emailAction.classList.toggle("d-none", isEmail)

      if (importCheckbox) {
        if (isIdentity) {
          importCheckbox.checked = false
          importCheckbox.disabled = true
        } else {
          importCheckbox.disabled = false
        }
      }

      if (importCheckboxWrapper) importCheckboxWrapper.classList.toggle("d-none", isIdentity)
      if (settingsSection) settingsSection.classList.toggle("d-none", isIdentity)
      if (exampleSection && isIdentity) exampleSection.classList.add("d-none")
    })
  }

  autoSelectNameAndEmail() {
    const nameIndex = this.headers.findIndex((header, index) => {
      return this.looksLikeNameHeader(header) && this.columnHasCompleteValues(index)
    })
    const emailIndex = this.headers.findIndex((header, index) => {
      return this.looksLikeEmailHeader(header) && index !== nameIndex
    })

    if (nameIndex >= 0) {
      this.nameColumnInputTarget.value = String(nameIndex)
    }

    if (emailIndex >= 0) {
      this.emailColumnInputTarget.value = String(emailIndex)
    }

    this.syncIdentityColumns()
  }

  columnHasCompleteValues(index) {
    if (!Array.isArray(this.dataRows) || this.dataRows.length === 0) return false

    return this.dataRows.every((row) => String(row[index] ?? "").trim() !== "")
  }

  looksLikeNameHeader(header) {
    const normalized = header.toLowerCase().replace(/[^a-z]/g, "")
    return ["name", "recipientname", "fullname", "athlete", "athletename"].includes(normalized)
  }

  looksLikeEmailHeader(header) {
    const normalized = header.toLowerCase().replace(/[^a-z]/g, "")
    return ["email", "recipientemail", "emailaddress"].includes(normalized)
  }

  updateSaveButtonState() {
    if (!this.hasSaveButtonTarget) return

    const nameSelected = this.nameColumnInputTarget.value !== ""
    const importCheckboxes = this.hasColumnsContainerTarget
      ? this.columnsContainerTarget.querySelectorAll('[data-import-checkbox="true"]')
      : []
    const importableSelected = Array.from(importCheckboxes).some((checkbox) => checkbox.checked && !checkbox.disabled)
    this.saveButtonTarget.disabled = !(nameSelected && importableSelected)
  }

  validateBeforeSubmit(event) {
    this.updateSaveButtonState()

    const nameIndex = this.nameColumnInputTarget.value
    if (nameIndex !== "" && !this.columnHasCompleteValues(Number(nameIndex))) {
      event.preventDefault()
      this.nameColumnInputTarget.value = ""
      this.syncIdentityColumns()
      this.updateSaveButtonState()
      this.showError("Name columns must have a value in every row. Please choose a different Name column.")
      return
    }

    if (this.saveButtonTarget.disabled) {
      event.preventDefault()
      this.showError("Select a Name column and at least one question column to import.")
    }
  }

  resetMapping() {
    this.headers = []
    this.dataRows = []
    this.rowCountValue = 0
    if (this.hasColumnsContainerTarget) {
      this.columnsContainerTarget.querySelectorAll('[data-name-column-tooltip="true"]').forEach((element) => {
        window.bootstrap?.Tooltip?.getInstance(element)?.dispose()
      })
      this.columnsContainerTarget.innerHTML = ""
    }
    if (this.hasMappingSectionTarget) this.mappingSectionTarget.classList.add("d-none")
    if (this.hasNameColumnInputTarget) this.nameColumnInputTarget.value = ""
    if (this.hasEmailColumnInputTarget) this.emailColumnInputTarget.value = ""
    this.updateSaveButtonState()
  }

  showError(message) {
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = message
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.remove("d-none")
  }

  clearError() {
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add("d-none")
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = ""
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/'/g, "&#39;")
  }
}
